import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Buildings, ShieldCheck, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { DataTable, FilterBar, FilterChip, StatusBadge, BulkActionBar } from '../../components/admin/AdminComponents';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminPublishers() {
  const [pubs, setPubs] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [vFilter, setVFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState([]);

  const fetchPubs = async (p = page) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (vFilter) params.append('verified', vFilter);
      params.append('skip', p * 50); params.append('limit', '50');
      const { data } = await axios.get(`${API_URL}/api/admin-console/publishers?${params}`, { withCredentials: true });
      setPubs(data.publishers || []); setTotal(data.total || 0);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchPubs(); }, [vFilter, page]);

  const verify = async (id) => {
    try {
      await axios.put(`${API_URL}/api/admin-console/publishers/${id}/verify`, {}, { withCredentials: true });
      toast.success('Publisher verified'); fetchPubs();
    } catch { toast.error('Failed'); }
  };

  const unverify = async (id) => {
    try {
      await axios.put(`${API_URL}/api/admin-console/publishers/${id}/unverify`, {}, { withCredentials: true });
      toast.success('Verification revoked'); fetchPubs();
    } catch { toast.error('Failed'); }
  };

  const remove = async (id, name, isSeed) => {
    const msg = isSeed
      ? `Archive SEED publisher "${name}"?\n\nSeed rows come from the initial catalog. Archiving hides them from the app but keeps every linked game's publisher name intact. This is a soft-delete you can restore later from the audit log if needed.`
      : `Archive publisher "${name}"? Linked games will keep their publisher name but the publisher will be hidden.`;
    // eslint-disable-next-line no-alert
    if (!window.confirm(msg)) return;
    try {
      await axios.delete(`${API_URL}/api/admin-console/publishers/${id}`, { withCredentials: true });
      toast.success('Publisher archived');
      fetchPubs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete publisher');
    }
  };

  const bulkVerify = async () => {
    try {
      await axios.post(`${API_URL}/api/admin-console/bulk/verify-publishers`, { ids: selected }, { withCredentials: true });
      toast.success(`Verified ${selected.length} publishers`); setSelected([]); fetchPubs();
    } catch { toast.error('Failed'); }
  };

  const columns = [
    {
      key: 'name', label: 'Publisher',
      render: (_, row) => (
        <div className="flex items-center gap-2">
          <Buildings size={16} weight="duotone" className="text-[#3B82F6]" />
          <div>
            <p className="text-[13px] text-white font-medium">{row.name}</p>
            <p className="text-[10px] text-[#444]">{row.slug}</p>
          </div>
        </div>
      ),
    },
    { key: 'website_url', label: 'Website', cellClass: 'hidden md:table-cell', render: v => <span className="text-[12px] text-[#555] truncate max-w-[200px] block">{v || '—'}</span> },
    {
      key: 'verified', label: 'Status',
      render: v => v ? (
        <span className="flex items-center gap-1 text-[11px] text-[#10B981]"><ShieldCheck size={13} weight="fill" /> Verified</span>
      ) : <StatusBadge status="pending" size="xs" />,
    },
    { key: 'games_count', label: 'Games', render: v => <span className="text-[12px] text-[#666] tabular-nums">{v}</span> },
    { key: 'visibility_status', label: 'Visibility', cellClass: 'hidden lg:table-cell', render: v => <StatusBadge status={v || 'public'} size="xs" /> },
    {
      key: 'actions', label: '',
      render: (_, row) => (
        <div className="flex gap-1.5" onClick={e => e.stopPropagation()}>
          {!row.verified ? (
            <button onClick={() => verify(row.id)}
              className="text-[11px] px-2.5 py-1 rounded-md bg-[#10B981]/10 text-[#10B981] font-medium hover:bg-[#10B981]/20"
              data-testid={`verify-pub-${row.id}`}>Verify</button>
          ) : (
            <button onClick={() => unverify(row.id)}
              className="text-[11px] px-2.5 py-1 rounded-md bg-white/5 text-[#555] font-medium hover:text-white"
              data-testid={`unverify-pub-${row.id}`}>Revoke</button>
          )}
          <button
            onClick={() => remove(row.id, row.name, row.is_seed)}
            className="text-[11px] px-2.5 py-1 rounded-md bg-[#EF4444]/10 text-[#EF4444] font-medium hover:bg-[#EF4444]/20 inline-flex items-center gap-1"
            data-testid={`delete-pub-${row.id}`}
            title={row.is_seed ? 'Archive seed publisher (soft-delete)' : 'Archive publisher'}
          >
            <Trash size={11} weight="bold" />
            Delete
          </button>
        </div>
      ),
    },
  ];

  return (
    <div data-testid="admin-publishers-page">
      <div className="mb-5">
        <h1 className="text-xl font-bold text-white tracking-tight" style={{ fontFamily: 'Unbounded' }}>Publishers</h1>
        <p className="text-[13px] text-[#444] mt-0.5">{total} publishers</p>
      </div>

      <FilterBar searchValue={search} onSearchChange={setSearch} onSearch={() => { setPage(0); fetchPubs(0); }}
        searchPlaceholder="Search publishers..." count={total} label="publishers">
        {[['', 'All'], ['true', 'Verified'], ['false', 'Unverified']].map(([v, l]) => (
          <FilterChip key={v} label={l} active={vFilter === v}
            onClick={() => { setVFilter(v); setPage(0); }} testId={`filter-verified-${v || 'all'}`} />
        ))}
      </FilterBar>

      <BulkActionBar count={selected.length} onClear={() => setSelected([])}
        actions={[{ label: 'Bulk Verify', color: '#10B981', onClick: bulkVerify }]}
      />

      <DataTable columns={columns} data={pubs} loading={loading} emptyMessage="No publishers found" emptyIcon={Buildings}
        selectable selectedIds={selected} onSelectionChange={setSelected} rowTestIdPrefix="publisher"
        page={page} pageSize={50} total={total} onPageChange={setPage}
      />
    </div>
  );
}
